import React, { useContext, useEffect, useState } from "react";
import { LAText, LATextInput, LAView } from "../../components";
import { IMAGES, SVG_IMAGES } from "../../assets";
import GlossaryFilter from "../../components/popup/GlossaryFilter";
import { GET_GLOSSARY } from "../../api/content";
import { STRING } from "../../constant";
import { BundleContext } from "../../context/BundleProvider";
import { USER_DEAFULT_KEY } from "../../constant/event";
import BackArrowIcon from "../../components/BackArrowIcon";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeProvider";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createLogger } from "../../utils/logger";
import { 
  faSearch, 
  faFilter, 
  faChevronDown, 
  faChevronUp,
  faChevronRight,
  faBook
} from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from "../../context/AuthProvider";

const Glossary = () => {
  const { selectedCertificate } = useContext(BundleContext);
  const { theme, currentTheme } = useContext(ThemeContext);
  const { loggingEnabled } = useContext(AuthContext);
  const [openItemId, setOpenItemId] = useState(null);
  const [glossaryState, setGlossaryState] = useState({ filterPopup: false });
  const [searchTxt, setSearchTxt] = useState("");
  const [glossaryData, setGlossaryData] = useState([]);
  const [selectedValue, setSelectedValue] = useState("ALL");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isFilterView, setFilterView] = useState(false);
  const navigate = useNavigate();
  
  const logger = createLogger(loggingEnabled);

  // Card animations
  const cardVariants = {
    hover: { 
      scale: 1.01,
      boxShadow: "0px 4px 10px rgba(0,0,0,0.05)",
      transition: { type: "spring", stiffness: 400, damping: 15 }
    }
  };

  // Helper function to get icon background color
  const getIconBg = () => {
    return currentTheme === 'light' ? theme?.icon_bg?.glossary_light : theme?.icon_bg?.glossary_dark;
  }

  // Helper function to get card background
  const getCardBackground = (iconBg) => {
    const baseColor = currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark;
    const darkerShade = currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark;   
    return {
      background: `linear-gradient(to right, ${baseColor}, ${darkerShade})`,
      borderLeft: `4px solid ${iconBg || (currentTheme === 'light' ? theme?.icon_bg?.glossary_light : theme?.icon_bg?.glossary_dark)}`
    };
  };

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  // Helper function for background color
  const getBackgroundColor = () => {
    return currentTheme === 'light' 
      ? theme?.background?.light
      : theme?.background?.dark;
  };

  useEffect(() => {
    var validaddress = /^[0-9].*$/;
    const GLOSSARY = GET_GLOSSARY(selectedCertificate);
    if (searchTxt) {
      if (selectedValue == "#") {
        const filter = GLOSSARY.filter(
          (data) =>
            data.value.toLowerCase().includes(searchTxt.toLowerCase()) &&
            validaddress.test(data?.value ?? " ")
        );
        setGlossaryData(filter);
      } else if (selectedValue != "ALL") {
        const filter = GLOSSARY.filter(
          (data) =>
            data?.value?.toLowerCase()?.includes(searchTxt?.toLowerCase()) &&
            selectedValue?.toLocaleLowerCase() ==
              (data?.value ?? " ")[0]?.toLocaleLowerCase()
        );
        setGlossaryData(filter);
      } else {
        const filter = GLOSSARY.filter((data) =>
          data?.value?.toLowerCase().includes(searchTxt?.toLowerCase())
        );
        setGlossaryData(filter);
      }
    } else {
      if (selectedValue == "#") {
        const filter = GLOSSARY.filter((data) =>
          validaddress.test(data?.value ?? " ")
        );
        setGlossaryData(filter);
      } else if (selectedValue != "ALL") {
        const filter = GLOSSARY.filter(
          (data) =>
            selectedValue?.toLocaleLowerCase() ==
            (data?.value ?? " ")[0]?.toLocaleLowerCase()
        );
        setGlossaryData(filter);
      } else {
        const filter = GLOSSARY;
        setGlossaryData(filter);
      }
    }
  }, [searchTxt, selectedValue, glossaryState?.filterPopup]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          className="rounded-lg border-2 p-2 mr-4 flex items-center justify-center"
          style={{ borderColor: currentTheme === "light" ? theme?.border_color?.light : theme?.border_color?.dark }}
          onClick={() => navigate(-1)}
        >
          <BackArrowIcon
            color={currentTheme === "light" ? theme?.text?.light : theme?.text?.dark}
          />
        </button>
        <h1 
          className="text-2xl font-semibold"
          style={{ color: getTextColor() }}
        >
          Glossary
        </h1>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <FontAwesomeIcon 
              icon={faSearch} 
              className="text-gray-400" 
            />
          </div>
          <input
            type="text"
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
            placeholder="Search by alphabets"
            onChange={(e) => setSearchTxt(e.target.value ? e.target.value : "")}
            style={{ 
              color: getTextColor()
            }}
          />
        </div>
        <button
          className="p-3 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: getIconBg() }}
          onClick={() => {
            logger.log("Filter button clicked, current state:", glossaryState.filterPopup);
            setGlossaryState((prev) => {
              const newState = {
                ...prev,
                filterPopup: !prev.filterPopup,
              };
              logger.log("Setting new state:", newState);
              return newState;
            });
          }}
        >
          <FontAwesomeIcon 
            icon={faFilter} 
            style={{ 
              color: getTextColor()
            }}
          />
        </button>
      </div>

      {/* Glossary Items */}
      {glossaryData.length > 0 ? (
        <div className="space-y-3">
          {glossaryData?.map((ele, idx) => {
            const isOpen = idx === openItemId;
            
            return (
              <motion.div
                key={idx}
                className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm"
                whileHover={!isOpen ? "hover" : undefined}
                variants={cardVariants}
              >
                <div style={getCardBackground(getIconBg())}>
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => setOpenItemId(isOpen ? null : idx)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        
                        <h2 
                          className="text-lg font-medium"
                          style={{ color: getTextColor() }}
                        >
                          {ele?.value}
                        </h2>
                      </div>
                      <FontAwesomeIcon 
                        icon={isOpen ? faChevronUp : faChevronDown} 
                        style={{ color: getTextColor() }}
                      />
                    </div>
                  </div>
                  
                  {/* Content area */}
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700">
                      <p 
                        className="mt-4"
                        style={{ color: getTextColor() }}
                      >
                        {ele?.description}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="flex items-center justify-center p-8 bg-white dark:bg-gray-800 rounded-xl" style={getCardBackground(getIconBg())}>
          <p style={{ color: getTextColor() }}>No Records Found</p>
        </div>
      )}

      {/* Filter Popup */}
      {glossaryState?.filterPopup && (
        <GlossaryFilter
          selectedValue={selectedValue}
          iconBg={getIconBg()}
          onCancel={() => {
            setGlossaryState((prev) => ({
              ...prev,
              filterPopup: false,
            }));
          }}
          onSelectItem={(item) => {
            setSelectedValue(item);
            setSelectedIndex(-1);
            setGlossaryState((prev) => ({
              ...prev,
              filterPopup: false,
            }));
          }}
        />
      )}
    </div>
  );
};

export default Glossary;